import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, TextArea, TextField } from '@/DesignSystem/Component';
import { useLoggedIn } from '@/Module/Auth/Hook';
import { authUpdatePersonAction } from '@/Module/Auth/Store/Action';
import type { IComment } from '@/Module/Comment/Model/DataModel';
import { createCommentAction } from '@/Module/Comment/Store/Action';
import { commentSetReplyIdAction } from '@/Module/Comment/Store/Slice/CommentSlice';
import { LINKS } from '@/Module/Core/Constant';
import { yup } from '@/Module/Core/Helper';
import { IconContact, IconWrite } from '@/Module/Icon';
import { useAppDispatch, useAppSelector } from '@/Module/Store/Hook';
interface IForm {
  description: string;
  firstName: string | undefined;
  lastName: string | undefined;
}
interface ICommentRegisterProps {
  parentId?: IComment['id'];
}
const CommentRegister: FC<ICommentRegisterProps> = ({
  parentId
}) => {
  const dispatch = useAppDispatch();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const router = useRouter();
  const loggedIn = useLoggedIn();
  const person = useAppSelector(state => state.auth.person);
  const isLoading = useAppSelector(state => state.comment.createIsLoading);
  const lessonSectionSelected = useAppSelector(state => state.lessonSection.selectedLessonSectionId)!;
  const needFullName = !person?.name || !person?.family;
  const schema = useMemo(() => {
    return yup.object({
      description: yup.string().required(),
      firstName: yup.string().test({
        name: 'firstNameValidate',
        test: (value, context) => {
          if (needFullName && !value?.length) return context.createError({
            message: 'وارد کردن نام الزامی است'
          });
          return true;
        }
      }),
      lastName: yup.string().test({
        name: 'lastNameValidate',
        test: (value, context) => {
          if (needFullName && !value?.length) return context.createError({
            message: 'وارد کردن نام خانوادگی الزامی است'
          });
          return true;
        }
      })
    });
  }, [needFullName, person]);
  const {
    control,
    handleSubmit,
    reset
  } = useForm<IForm>({
    resolver: yupResolver(schema)
  });
  const onSubmit = handleSubmit(data => {
    if (!loggedIn) {
      enqueueSnackbar({
        variant: 'warning',
        message: 'برای ارسال نظر لطفا ابتدا وارد حساب کاربری خود شوید.'
      });
      router.push(`${LINKS.AUTH.LOGIN}?redirectUrl=${encodeURI(router.asPath)}`);
      return;
    }
    new Promise(resolve => {
      if (needFullName && person) {
        dispatch(authUpdatePersonAction({
          fieldId: person.fieldId,
          fundametalId: person.fundametalId,
          gender: person.gender,
          gradeId: person.gradeId,
          isComplete: person.isComplete,
          name: data.firstName ?? person.name,
          family: data.lastName ?? person.family,
          id: person.id,
          stateId: person.stateId,
          cityId: person.cityId,
          birthDate: person.birthDate
        })).unwrap().finally(() => {
          resolve(true);
        });
      }
      resolve(true);
    }).then(async () => {
      await dispatch(createCommentAction({
        commentTypeID: 3,
        commentText: data.description,
        entityId: lessonSectionSelected,
        parentId
      })).unwrap().then(() => {
        reset();
        enqueueSnackbar({
          variant: 'success',
          message: 'نظر شما ثبت گردید'
        });
        if (parentId) {
          dispatch(commentSetReplyIdAction(null));
        }
      });
    });
  });
  return <Box color="lightOrange" className="mb-10 w-full flex-1 border-b-4 border-r-4 border-orangeThree  p-4 shadow-theme sm:p-8 " data-sentry-element="Box" data-sentry-component="CommentRegister" data-sentry-source-file="CommentRegister.tsx">
      <form onSubmit={onSubmit} className="flex w-full flex-row flex-wrap justify-end gap-4">
        <TextArea control={control} disabled={isLoading} name="description" rows={6} label="نظر خود را بنویسید." prefix={<IconWrite />} className=" w-full " data-sentry-element="TextArea" data-sentry-source-file="CommentRegister.tsx" />

        {needFullName && <div className="flex w-full flex-wrap  items-center gap-4">
            <TextField control={control} disabled={isLoading} name="firstName" type="text" label="نام خود را بنویسید." prefix={<IconContact />} className="min-w-full flex-1 sm:min-w-[230px] " />
            <TextField control={control} disabled={isLoading} name="lastName" type="text" label="نام خانوادگی خود را بنویسید." prefix={<IconContact />} className=" min-w-full flex-1 sm:min-w-[230px] " />
          </div>}

        <Button className="relative mt-4 w-full place-self-start self-end justify-self-end md:w-auto" variant="shadow" color="orange" radius="circle" size="large" type="submit" isLoading={isLoading} data-sentry-element="Button" data-sentry-source-file="CommentRegister.tsx">
          ارسال نظر
        </Button>
      </form>
    </Box>;
};
export default CommentRegister;